import type { UpdateUserRequest, User } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useUpdateUserMutation } from '../../graphql/hooks/useUpdateUserMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { UserForm } from './UserForm';
import { userSchema } from './UserInviteModal';

type FormValues = UpdateUserRequest & {
  organizationId: string;
};

interface Props {
  user: User;
}

export const UserEditModal = createModal<Props>((props) => {
  const { modal, user } = props;
  const { hide, visible, remove } = modal;
  const { organization, successToast, errorToast } = useAppContext();

  const mutation = useUpdateUserMutation();

  const organizationMembership = user.organizationMemberships.find(
    (membership) => membership.organizationId === organization.id,
  );

  const defaultValues = React.useMemo(() => {
    return {
      organizationId: organization.id,
      name: user.name,
      email: user.email,
      title: user.title,
      practitionerNumber: user.practitionerNumber,
      picture: user.picture,
      roles: organizationMembership?.roles ?? [],
      locations: (organizationMembership?.locations ?? []).map((l) => l.id),
      settings: user.settings,
      featureFlags: user.featureFlags,
    };
  }, [
    organization.id,
    organizationMembership?.locations,
    organizationMembership?.roles,
    user.email,
    user.name,
    user.picture,
    user.practitionerNumber,
    user.title,
    user.settings,
    user.featureFlags,
  ]);

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(userSchema),
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const request = P.pickBy(values, (value, key) => {
        return !P.isEqual(value, defaultValues[key]);
      });

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          userId: user.id,
          request: P.deepTrim(request),
        })
        .then(() => {
          hide();
          successToast('User updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating user: ${err.message}`);
        });
    },
    [mutation, organization.id, user.id, defaultValues, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a user'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <UserForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
